import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Barbell, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { loginPhotoSrc } from "@/pages/LoginPhotos";

const FALLBACK_PHOTO = "https://static.prod-images.emergentagent.com/jobs/0954cda1-8297-4b7e-bbf2-b7d5eb35734e/images/8e50cbde0fc5039978e4c818570fb50b715541ccddf261e8719315d0528dc43b.jpeg";

export default function Login() {
  const { user, login, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoLoaded, setPhotoLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/login-photos/current`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setPhoto(d?.photo ? { src: loginPhotoSrc(d.photo.url), alt: d.photo.label } : { src: FALLBACK_PHOTO }))
      .catch(() => setPhoto({ src: FALLBACK_PHOTO }));
  }, []);

  useEffect(() => {
    if (ready && user) nav("/", { replace: true });
  }, [ready, user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
      return;
    }
    toast.success("Welcome back");
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden md:block">
        <div className="absolute inset-0 bg-moss" />
        {photo && (
          <img
            src={photo.src}
            alt={photo.alt || "HealthSpan athletes training"}
            onLoad={() => setPhotoLoaded(true)}
            onError={() => { if (photo.src !== FALLBACK_PHOTO) setPhoto({ src: FALLBACK_PHOTO }); }}
            className={`absolute inset-0 h-full w-full object-cover object-[center_30%] transition-opacity duration-700 ${photoLoaded ? "opacity-100" : "opacity-0"}`}
            data-testid="login-photo"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-moss/80 via-moss/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur grid place-items-center">
              <Barbell size={22} weight="bold" />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold">HealthSpan Training</div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/70">Coach · Client Platform</div>
            </div>
          </div>
          <div className="max-w-md space-y-3">
            <div className="text-xs uppercase tracking-[0.3em] text-white/80">Our mission</div>
            <h2 className="font-heading text-3xl leading-tight">
              Strength, mobility, and independence — for the long road ahead.
            </h2>
            <p className="text-sm text-white/85">
              Build weekly programs, track every rep and set, and celebrate the PRs that matter most to your athletes.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="hs-label mb-3">Sign in</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">Welcome Back to your HealthSpan Training Program.</h1>
            <p className="text-ink-2 mt-2">
              Train longer, stronger, smarter. Log in to manage programs and records.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="hs-label">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-xl"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="hs-label">Password</Label>
                <Link to="/forgot-password" className="text-xs text-moss hover:text-moss-hover hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 rounded-xl"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-medium"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : (<span className="inline-flex items-center gap-2">Enter the gym <ArrowRight size={16} /></span>)}
          </Button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-line" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-bone px-3 text-[11px] uppercase tracking-[0.2em] text-ink-3">or</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-12 rounded-xl bg-white hover:bg-muted2 text-ink border border-line font-medium inline-flex items-center justify-center gap-2"
            data-testid="google-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.3 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2c-2 1.5-4.5 2.5-7.2 2.5-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Sign in with Google
          </Button>

          <p className="text-xs text-ink-3">
            Accounts are created by your head coach. If you don't have one, please contact your trainer.
          </p>
        </form>
      </div>
    </div>
  );
}
